// Get the roof area of a structure
export const getRoofArea = (structure) => structure?.roof?.structure_roof_area?.value ?? 0;

// Get the roof area units
export const getRoofAreaUnits = (structure) => structure?.roof?.structure_roof_area?.unit || '';

// Get the roof squares count
export const getRoofAreaSquares = (primaryStructureData) => primaryStructureData?.roof?.structure_roof_area_squares?.value ?? 0;

// #genai Extract the reduced result directly while preserving undefined for missing structures.
export const getPrimaryStructureData = (propertyData) => propertyData?.structures?.reduce((maxStruct, curr) => {
  const { maxArea } = maxStruct;
  const currArea = getRoofArea(curr);
  return currArea > maxArea ? ({ maxArea: currArea, primaryStructureData: curr }) : maxStruct;
}, { maxArea: 0, primaryStructureData: propertyData?.structures?.[0] })?.primaryStructureData;

// Extract minimum and maximum eave height from the structure_eave_height object
export const getMinMaxEaveValue = (structureData) => {
  const eaveHeightValue = structureData?.structure_eave_height?.value;

  // Return empty object if eaveHeightValue is empty or doesn't exist
  if (!eaveHeightValue || Object.keys(eaveHeightValue).length === 0) {
    return {};
  }

  const { min: minEaveValue, max: maxEaveValue } = Object.values(eaveHeightValue).reduce((acc, value) => (
    value !== null ? { max: Math.max(acc.max, value), min: Math.min(acc.min, value) } : acc
  ), { max: Number.MIN_SAFE_INTEGER, min: Number.MAX_SAFE_INTEGER });

  return { minEaveValue, maxEaveValue };
};
export const getPropertyDataDisplayItems = (structure, t) => {
  // Check if roof age data exists, return 'N/A' if not available
  const roofAgeValue = structure?.roof?.structure_roof_age?.value;
  const roofAgeUnit = structure?.roof?.structure_roof_age?.unit;
  const roofAge = roofAgeValue ? `${roofAgeValue} ${roofAgeUnit}` : 'N/A';

  // Extract minimum and maximum eave height from the structure_eave_height object
  const { minEaveValue, maxEaveValue } = getMinMaxEaveValue(structure);
  const minEaveHeight = minEaveValue ? `${minEaveValue} feet` : 'N/A';
  const maxEaveHeight = maxEaveValue ? `${maxEaveValue} feet` : 'N/A';

  const roofAreaInSquares = Math.round(getRoofArea(structure)) / 100;
  return ([
    {
      label: t('property_data__roof_age'),
      value: roofAge,
      tooltipTitle: t('property_data__roof_age_tooltip'),
    },
    {
      label: t('property_data__roof_area_estimate'),
      value: `${roofAreaInSquares} Squares`,
      tooltipTitle: t('property_data__roof_area_estimate_tooltip'),
    },
    {
      label: t('property_data__roof_age_reason'),
      value: structure?.roof?.structure_roof_age?.reasons?.[0] || 'N/A',
      tooltipTitle: t('property_data__roof_age_reason_tooltip'),
    },
    {
      label: t('property_data__roof_condition'),
      value: structure?.roof?.structure_roof_condition_rating?.value || 'N/A',
      tooltipTitle: t('property_data__roof_condition_tooltip'),
    },
    {
      label: t('property_data__predominant_pitch'),
      value: structure?.roof?.structure_roof_predominant_pitch?.value
        ? `${structure.roof.structure_roof_predominant_pitch.value}/12`
        : 'N/A',
      tooltipTitle: t('property_data__predominant_pitch_tooltip'),
    },
    {
      label: t('property_data__min_eave_height'),
      value: minEaveHeight,
      tooltipTitle: t('property_data__min_eave_height_tooltip'),
    },
    {
      label: t('property_data__max_eave_height'),
      value: maxEaveHeight,
      tooltipTitle: t('property_data__max_eave_height_tooltip'),
    },
    {
      label: t('property_data__roof_material'),
      value: structure?.roof?.structure_roof_material_primary?.value || 'N/A',
      tooltipTitle: t('property_data__roof_material_tooltip'),
    },
    {
      label: t('property_data__solar_panels'),
      value: structure?.roof?.structure_roof_solar_panel_presence?.value || 'N/A',
      tooltipTitle: t('property_data__solar_panels_tooltip'),
    },
    {
      label: t('property_data__roof_facet_count'),
      value: structure?.roof?.structure_roof_facet_count?.value || 'N/A',
      tooltipTitle: t('property_data__roof_facet_count_tooltip'),
    },
  ]);
};
